import { Link, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import Reveal from '@/components/Reveal';
import SEO from '@/components/SEO';
import { SectionLabel, PrimaryButton, TextLink } from '@/components/Button';
import { INSIGHT_META } from '@/data/insights';

export default function InsightArticle() {
  const { slug } = useParams();
  const { t } = useTranslation(['articles', 'insights', 'common']);
  const idx = INSIGHT_META.findIndex((a) => a.slug === slug);

  if (idx === -1) {
    return <Navigate to="/insights" replace />;
  }

  const meta = INSIGHT_META[idx];
  const prev = idx > 0 ? INSIGHT_META[idx - 1] : null;
  const next = idx < INSIGHT_META.length - 1 ? INSIGHT_META[idx + 1] : null;

  const title = t(`${meta.slug}.title`, { ns: 'articles' });
  const excerpt = t(`${meta.slug}.excerpt`, { ns: 'articles' });
  const body = t(`${meta.slug}.body`, { ns: 'articles', returnObjects: true }) || [];

  return (
    <Layout>
      <SEO title={title} description={excerpt} />

      <section className="pt-20 md:pt-24" data-testid="article-hero">
        <div className="w-full max-w-content mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="overflow-hidden aspect-[21/9] bg-brand-surface">
              <img
                src={meta.image}
                alt={title}
                loading="eager"
                className="w-full h-full object-cover saturate-[0.92]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <article className="py-16 md:py-24">
        <div className="max-w-content mx-auto px-6 lg:px-10">
          <div className="max-w-prose mx-auto">
            <Reveal>
              <SectionLabel>{t('hero.eyebrow', { ns: 'insights' })}</SectionLabel>
              <h1 className="mt-5 font-display text-3xl md:text-5xl font-semibold text-brand-primary leading-[1.1] tracking-tight">
                {title}
              </h1>
              <p className="mt-6 text-sm text-brand-muted">
                {t('byAuthor', { ns: 'common' })} · {t('readingTime', { ns: 'common', min: meta.minutes })}
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12 prose-article" data-testid="article-body">
                {body.map((block, i) =>
                  block.type === 'h2' ? (
                    <h2 key={i}>{block.content}</h2>
                  ) : (
                    <p key={i}>{block.content}</p>
                  )
                )}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-20 p-8 md:p-10 bg-brand-surface border border-black/5">
                <p className="eyebrow text-brand-accent">{t('cta.label', { ns: 'insights' })}</p>
                <p className="mt-3 font-display text-xl md:text-2xl text-brand-primary leading-snug">
                  {t('cta.headline', { ns: 'insights' })} {t('cta.body', { ns: 'insights' })}
                </p>
                <div className="mt-6">
                  <PrimaryButton to="/contact" testId="article-cta">{t('cta.getInTouch', { ns: 'common' })}</PrimaryButton>
                </div>
              </div>
            </Reveal>

            <div className="mt-16 pt-10 border-t border-black/10 flex items-center justify-between gap-6 text-sm flex-wrap">
              <TextLink to="/insights" testId="article-all-link">{t('cta.backToInsights', { ns: 'common' })}</TextLink>
              <div className="flex gap-6">
                {prev && (
                  <Link
                    to={`/insights/${prev.slug}`}
                    className="text-brand-accent hover:text-brand-primary link-underline"
                    data-testid="article-prev"
                  >
                    ← {t('cta.previous', { ns: 'common' })}
                  </Link>
                )}
                {next && (
                  <Link
                    to={`/insights/${next.slug}`}
                    className="text-brand-accent hover:text-brand-primary link-underline"
                    data-testid="article-next"
                  >
                    {t('cta.next', { ns: 'common' })} →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}
